import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import AcUnitIcon from '@mui/icons-material/AcUnit'
import BookOnlineIcon from '@mui/icons-material/BookOnline'
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import Logo from '../../../assets/logo.png'
import { REGISTER_ROUTE } from '../../../config'
import { DivMainImg, GridItemComponent } from '../styled'

const FEATURE_CHIPS = [
  'Climate-Controlled Units',
  'Drive-Up Storage',
  'Covered Boat & RV Parking',
]

const TRUST_ITEMS = [
  {
    title: 'Climate options',
    value: 'Comfort for furniture, inventory, and seasonal items.',
    icon: <AcUnitIcon fontSize="small" />,
  },
  {
    title: 'Vehicle storage',
    value: 'Covered and open parking for boats, trailers, and RVs.',
    icon: <DirectionsCarFilledIcon fontSize="small" />,
  },
  {
    title: 'Simple and secure',
    value: 'Reserve online, pay monthly, and access your space with ease.',
    icon: <ShieldOutlinedIcon fontSize="small" />,
  },
]

const HERO_IMAGE =
  'https://images.website.storedge.com/eyJrZXkiOiI2MGYwNDBiMy1mYmNkLTRjMmQtODkyZi02MjY3M2IzZWM3ZWYvMjAxNjA2MTAtMTA0MjA4LmpwZyIsImVkaXRzIjp7InJlc2l6ZSI6eyJ3aWR0aCI6MTkwMCwiZml0IjoiY292ZXIifX19'

export const HomeTopRowComponent: React.FC<{
  onChangeRoute: (path: string) => void
}> = ({ onChangeRoute }) => (
  <GridItemComponent
    sx={{
      overflow: 'hidden',
      border: '1px solid rgba(173, 223, 255, 0.1)',
      background:
        'linear-gradient(180deg, rgba(14, 19, 25, 0.98) 0%, rgba(8, 12, 18, 1) 100%)',
      boxShadow: '0 22px 48px rgba(0, 0, 0, 0.22)',
    }}
  >
    <DivMainImg sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
      <Box
        component="img"
        src={HERO_IMAGE}
        alt="Just Lock It storage property"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: 0.34,
          transform: 'scale(1.04)',
          filter: 'saturate(0.9) contrast(1.04)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(8, 12, 18, 0.78) 0%, rgba(8, 12, 18, 0.74) 32%, rgba(8, 12, 18, 0.66) 58%, rgba(8, 12, 18, 0.9) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at top left, rgba(90, 181, 227, 0.22) 0%, rgba(90, 181, 227, 0) 34%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: { xs: 700, md: 620 },
          p: { xs: 2.25, sm: 3, lg: 4 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: { xs: 2.5, md: 3.25 },
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.1,
            alignSelf: 'flex-start',
            pr: 1.4,
            py: 0.8,
            pl: 0.9,
            border: '1px solid rgba(173, 223, 255, 0.14)',
            backgroundColor: 'rgba(10, 18, 28, 0.42)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Box
            component="img"
            src={Logo}
            alt="Just Lock It logo"
            sx={{
              width: 42,
              height: 42,
              objectFit: 'contain',
              filter: 'drop-shadow(0 6px 14px rgba(0, 0, 0, 0.24))',
            }}
          />
          <Box>
            <Typography
              sx={{
                color: 'rgba(192, 232, 255, 0.98)',
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
              }}
            >
              Self Storage in Schulenburg
            </Typography>
            <Typography
              sx={{
                color: '#f7fbff',
                fontSize: '0.92rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Just Lock It
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            maxWidth: 760,
            px: { xs: 0, md: 0.5 },
          }}
        >
          <Typography
            sx={{
              color: '#f7fbff',
              fontSize: { xs: '2.1rem', sm: '2.9rem', lg: '3.7rem' },
              fontWeight: 800,
              lineHeight: 0.96,
              letterSpacing: '-0.025em',
              textAlign: 'left',
              textShadow: '0 4px 18px rgba(0,0,0,0.3)',
            }}
          >
            Clean, secure storage with room for the things that matter.
          </Typography>
          <Typography
            sx={{
              mt: 1.6,
              color: 'rgba(239, 247, 255, 0.88)',
              fontSize: { xs: '1rem', sm: '1.08rem' },
              fontWeight: 500,
              lineHeight: 1.65,
              textAlign: 'left',
              maxWidth: 590,
            }}
          >
            Store furniture, business inventory, seasonal gear, boats, or RVs
            with climate-controlled units, standard storage, and convenient
            parking right off US 77.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 2,
            }}
          >
            {FEATURE_CHIPS.map((chip) => (
              <Box
                key={chip}
                sx={{
                  px: 1.15,
                  py: 0.7,
                  border: '1px solid rgba(173, 223, 255, 0.14)',
                  backgroundColor: 'rgba(10, 18, 28, 0.38)',
                  color: '#d8f3ff',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {chip}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.1,
              mt: 2.2,
            }}
          >
            <Button
              onClick={() => onChangeRoute(REGISTER_ROUTE)}
              variant="contained"
              color="info"
              startIcon={<BookOnlineIcon />}
              size="large"
              sx={{ minWidth: { sm: 220 } }}
            >
              Reserve Units
            </Button>
            <Button
              variant="outlined"
              href="tel:+19793099335"
              startIcon={<LocalPhoneIcon />}
              size="large"
              sx={{
                borderColor: 'rgba(135, 205, 255, 0.48)',
                backgroundColor: 'rgba(10, 18, 28, 0.28)',
                color: '#d8f3ff',
                '&:hover': {
                  borderColor: 'rgba(135, 205, 255, 0.8)',
                  backgroundColor: 'rgba(10, 18, 28, 0.42)',
                },
              }}
            >
              979-309-9335
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.1,
          }}
        >
          {TRUST_ITEMS.map(({ title, value, icon }) => (
            <Box
              key={title}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.15,
                px: 1.35,
                py: 1.25,
                border: '1px solid rgba(173, 223, 255, 0.12)',
                backgroundColor: 'rgba(10, 18, 28, 0.4)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  color: '#9edaff',
                  backgroundColor: 'rgba(37, 74, 103, 0.34)',
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: '#f7fbff',
                    fontSize: '0.96rem',
                    fontWeight: 700,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    color: 'rgba(230, 240, 250, 0.76)',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </DivMainImg>
  </GridItemComponent>
)
